/**
 * What an organization has on, and what it has just finished (GH-770).
 *
 * ## Why the organization page lists events at all
 *
 * `/o/<id>` used to know about exactly one event — whichever one the directory's `LIVE_EVENT`
 * lateral picked — so an organization with a fundraiser tonight, a dinner next month and three
 * finished campaigns appeared to a donor as an organization with one thing happening. The owner's
 * rule for this page is that it is about the **organization**: its record, its people, and its
 * calendar. Two lists, clearly separated, because "coming up" and "already happened" are different
 * questions and a single list ordered by date answers neither well.
 *
 * ## The two links on a row, and the one that is conditional
 *
 * Every row links to `/e/<eventId>`, the donor's own event page. That link is unconditional: an
 * ended event still has a page worth reaching, showing the final total.
 *
 * **Check-in is offered only on a `live` row.** `POST /v1/donor/check-ins` refuses any event whose
 * status is not `live` (`services/api/src/donor-routes.ts`), so on a future or finished event the
 * link would land a donor on a screen whose only job would be to tell them no. A control that
 * exists to be refused is worse than no control, and this is the one behaviour on this component
 * with a test of its own.
 *
 * The check-in destination is `/c/<eventId>` — the same address the QR code on the hall's wall
 * encodes (`apps/live-display/src/JoinQr.tsx`). One act, one URL: a second path would be a second
 * thing to keep in step with the printed code.
 *
 * ## What a row deliberately does not carry
 *
 * **No money.** The list payload has no goal and no raised figure by construction
 * (`publicOrganizationEventSchema`): a goal with no numerator invites "of $50,000" with nothing in
 * front of it, and a numerator per row would be one `/goal` read each on a public route. The one
 * event this page shows progress for is the main event, above, read once.
 *
 * **No hero image.** The wire carries `heroImageUrl` because the event page and the ticket page
 * both draw one; a column of photographs here would turn a calendar into a gallery and push the
 * organization's own record below three screens of scrolling. If that changes it is a design
 * decision, not a field somebody wired up because it was available.
 *
 * **No attendance, no ticket count, nothing about who was there.** That is the constraint the
 * whole public directory read is built around.
 */
import type { PublicOrganizationEvent } from "@givehere/contracts";
import { echoes } from "./copy-echo.js";

export function OrgEventList({
  heading,
  events,
}: {
  /** Names the region: "What's on", "Recently". Rendered as this page's overline slot. */
  heading: string;
  events: readonly PublicOrganizationEvent[];
}) {
  /*
    Absent, not empty. An organization between campaigns is an ordinary state (SPEC.md §6.1), and
    "No upcoming events" printed under a heading is a sentence about the organization rather than
    about the list. The page says that once, in the give block, in the server's own framing.
  */
  if (events.length === 0) return null;

  return (
    <section className="landing__block">
      <h2 className="landing__section">{heading}</h2>
      <ul className="orgevents">
        {events.map((event) => (
          <OrgEventRow key={event.id} event={event} />
        ))}
      </ul>
    </section>
  );
}

/** One event: what it is, when, where, and the one or two doors out of it. */
function OrgEventRow({ event }: { event: PublicOrganizationEvent }) {
  const startsAt = when(event.startsAt);
  const where = [event.location, event.address].filter(Boolean).join(" \u00b7 ");

  return (
    <li className="orgevent">
      <p className="orgevent__name" dir="auto">
        {event.name}
      </p>
      {startsAt && <p className="orgevent__when">{startsAt}</p>}
      {/*
        Suppressed when it only echoes the name above it — the same rule the event page applies to
        its own subtitle, for the same reason: an organizer may name an event and state its purpose
        identically, and the row then printed the same words twice.
      */}
      {event.purpose && !echoes(event.purpose, event.name) && (
        <p className="orgevent__purpose" dir="auto">
          {event.purpose}
        </p>
      )}
      {/*
        The room and the street, in that order and joined only when both exist. `location` is the
        organizer's own words for the place ("Main hall", "Parish centre") and is never geocoded
        (migration 0006); `address` is the postal address of a public venue being advertised. They
        answer different questions — which room, and how to get there — so neither stands in for
        the other.
      */}
      {where && (
        <p className="orgevent__where" dir="auto">
          {where}
        </p>
      )}
      <p className="orgevent__links">
        <a className="link" href={`/e/${encodeURIComponent(event.id)}`}>
          Open the event page
        </a>
        {/*
          Only while it is running. The server refuses a check-in for anything but a `live` event,
          so on a future or finished row this link would open a screen whose only job would be to
          say no.
        */}
        {event.status === "live" && (
          <a className="link" href={`/c/${encodeURIComponent(event.id)}`}>
            Check in
          </a>
        )}
      </p>
    </li>
  );
}

/**
 * When it starts, in the reader's own locale, or nothing.
 *
 * `dateStyle: "medium"` and `timeStyle: "short"` rather than a hand-built string: an organization
 * publishing to a congregation that reads dates day-first should see them day-first, and the only
 * component that knows which that is is the browser's. The same pairing `TicketsPage` already
 * uses, one step shorter, because a list of six rows does not need six weekday names spelled out.
 *
 * A null start is ordinary rather than an error — an organizer may announce a campaign before
 * fixing a date — and the row then carries its name, its purpose and its links with no when-line
 * at all. Inventing "Date to be confirmed" would be this page speaking for the organization.
 */
function when(startsAt: string | null): string | null {
  if (startsAt === null) return null;
  const at = new Date(startsAt);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}
